import { BOX_INTERVALS, DEFAULT_LEVEL, LEVELS } from "../config/defaults";
import { DIFFICULTY_MODES, type DifficultyKey } from "../config/modes";
import type { Card, CardStat, CefrLevel, StatsMap } from "../types";

export function intervalForBox(box: number): number {
  const b = Math.min(Math.max(box || 1, 1), 5);
  return BOX_INTERVALS[b - 1]!;
}

// Pick a target level offset relative to the user's estimated CEFR
// level, using the current difficulty mode's distribution.
export function rollTargetLevel(
  userLevel: CefrLevel,
  difficulty: DifficultyKey,
): CefrLevel {
  const userIdx = LEVELS.indexOf(userLevel);
  const mode = DIFFICULTY_MODES[difficulty] || DIFFICULTY_MODES.adaptive;
  const buckets = mode.buckets;
  let r = Math.random();
  let chosen = 0;
  for (const b of buckets) {
    if (r < b.weight) {
      chosen = b.off;
      break;
    }
    r -= b.weight;
  }
  const idx = Math.max(0, Math.min(LEVELS.length - 1, userIdx + chosen));
  return LEVELS[idx]!;
}

type PoolEntry = { card: Card; dueAt: number; box: number };

/**
 * Adaptive Leitner card picker. Biases picks toward the user's estimated
 * level (using the active difficulty's distribution), then within the
 * chosen level applies Leitner priority: unseen → due (lowest box first)
 * → future (soonest first). The previously shown card is excluded to
 * avoid immediate repeats.
 */
export function pickNextCard(
  cards: Card[],
  stats: StatsMap,
  lastId: string | null,
  userLevel: CefrLevel,
  difficulty: DifficultyKey,
  now: number = Date.now(),
): Card {
  const tryPool = (pool: Card[]): Card | null => {
    const neverSeen: Card[] = [];
    const due: PoolEntry[] = [];
    const future: PoolEntry[] = [];
    for (const c of pool) {
      if (c.id === lastId && pool.length > 1) continue;
      const s = stats[c.id];
      if (!s || !s.attempts) {
        neverSeen.push(c);
      } else {
        const dueAt = s.dueAt || 0;
        const box = s.box || 1;
        (dueAt <= now ? due : future).push({ card: c, dueAt, box });
      }
    }
    if (neverSeen.length) {
      return neverSeen[Math.floor(Math.random() * neverSeen.length)]!;
    }
    if (due.length) {
      due.sort((a, b) => a.box - b.box || a.dueAt - b.dueAt);
      const minBox = due[0]!.box;
      const sameBox = due.filter((d) => d.box === minBox);
      return sameBox[Math.floor(Math.random() * sameBox.length)]!.card;
    }
    if (future.length) {
      future.sort((a, b) => a.dueAt - b.dueAt);
      return future[0]!.card;
    }
    return null;
  };

  // Try up to 3 level rolls before giving up and using the global pool.
  // Require at least MIN_LEVEL_POOL cards at the rolled level: below this
  // the `lastId` exclusion in tryPool (pool.length > 1) produces a visible
  // 2-card cycle — the symptom surfaced when tag filters narrow the input
  // pool enough that some CEFR levels end up with only 1–2 matching cards.
  const MIN_LEVEL_POOL = 3;
  for (let attempt = 0; attempt < 3; attempt++) {
    const target = rollTargetLevel(userLevel || DEFAULT_LEVEL, difficulty);
    const pool = cards.filter((c) => (c.level || DEFAULT_LEVEL) === target);
    if (pool.length >= MIN_LEVEL_POOL) {
      const picked = tryPool(pool);
      if (picked) return picked;
    }
  }
  // Fallback: ignore level. Reached when every rolled level was too sparse
  // (tag filter active on a thin dataset) or the 3 rolls all missed cards.
  const picked = tryPool(cards);
  if (picked) return picked;
  return cards[Math.floor(Math.random() * cards.length)]!;
}

// Apply the result of an attempt to a card's stat, mutating and returning
// the updated stat. Right answers promote the box (capped at 5) and push
// dueAt forward by the box interval; wrong answers demote to box 1.
export function applyResult(
  prev: CardStat | undefined,
  correct: boolean,
  now: number = Date.now(),
): CardStat {
  const s: CardStat = prev
    ? { ...prev }
    : { attempts: 0, correct: 0, box: 1, dueAt: 0, lastSeenAt: 0, lastFailAt: 0 };
  s.attempts += 1;
  s.lastSeenAt = now;
  if (correct) {
    s.correct += 1;
    s.box = Math.min(5, (s.box || 1) + 1);
  } else {
    s.box = 1;
    s.lastFailAt = now;
  }
  s.dueAt = now + intervalForBox(s.box);
  return s;
}
